import {
  FacebookFilled,
  InstagramFilled,
  LinkedinFilled,
  MailOutlined,
  MediumCircleFilled,
  TwitterOutlined,
} from '@ant-design/icons';
import { useQuery } from '@apollo/client';
import {
  A3_BADGE_PATH,
  A3BadgeNames,
  A3UserStatistics,
  GET_A3_STATISTICS_FOR_USER,
  GET_ME,
  GET_USER_BY_ID,
  Me,
  User,
} from '@frontend/data-access';
import { Button, Col, Divider, Empty, Row, Spin, Typography } from 'antd';
import getConfig from 'next/config';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useCallback, useMemo } from 'react';

import CompanyAvatar from '../company-avatar/company-avatar';
import UserHeaderSection from '../user-header-section/user-header-section';

const { Text } = Typography;

export function UserInfo({ user }: { user: User }) {
  const { publicRuntimeConfig } = getConfig();
  const { t: tcommon } = useTranslation('common');
  const router = useRouter();
  const { data: meData } = useQuery<{ me: Me }>(GET_ME);
  const { data, loading } = useQuery<{
    findUser: Me;
  }>(GET_USER_BY_ID, {
    variables: {
      id: user?.id,
    },
  });
  const { data: a3StatisticsData } = useQuery<{
    getA3StatisticsForUser: A3UserStatistics;
  }>(GET_A3_STATISTICS_FOR_USER, {
    skip: !user?.id || meData?.me.role === 'EXTERNAL_USER',
    variables: {
      userId: user?.id,
    },
  });
  const getCompanyLink = useCallback(
    (id: number) => {
      if (
        router.basePath.includes(
          process.env['NEXT_PUBLIC_ADMIN_BASE_PATH'] || '/adminftb'
        )
      ) {
        // Admin
        return '/company-management/' + id;
      }
      return '/company/' + id;
    },
    [router.basePath]
  );

  const email = useMemo(() => {
    if (meData?.me.role === 'ADMIN') {
      return data?.findUser.email;
    }
    return meData?.me.role !== 'EXTERNAL_USER' &&
      !(!data?.findUser.emailHidden || data?.findUser.email)
      ? data?.findUser.email
      : null;
  }, [meData?.me.role, data?.findUser.emailHidden, data?.findUser.email]);

  const companies = data?.findUser?.companies.filter(
    (company) => company.approvalStatus === 'approved'
  );

  const a3Data: Record<string, string> = {
    [A3BadgeNames.Novice]: `${publicRuntimeConfig.staticFolder}${A3_BADGE_PATH?.NOVICE}`,
    [A3BadgeNames.Apprentice]: `${publicRuntimeConfig.staticFolder}${A3_BADGE_PATH?.APPRENTICE}`,
    [A3BadgeNames.Pro]: `${publicRuntimeConfig.staticFolder}${A3_BADGE_PATH?.PRO}`,
    [A3BadgeNames.Master]: `${publicRuntimeConfig.staticFolder}${A3_BADGE_PATH?.MASTER}`,
  };

  const a3Badge = data?.findUser?.badge?.length
    ? a3Data[data.findUser.badge[data.findUser.badge.length - 1].name]
    : null;

  const totalStatistics = useMemo(() => {
    if (!a3StatisticsData?.getA3StatisticsForUser) {
      return 0;
    }
    return (
      (a3StatisticsData?.getA3StatisticsForUser?.owned || 0) +
      (a3StatisticsData?.getA3StatisticsForUser?.sponsored || 0) +
      (a3StatisticsData?.getA3StatisticsForUser?.coached || 0) +
      (a3StatisticsData?.getA3StatisticsForUser?.member || 0)
    );
  }, [a3StatisticsData]);

  const items = [
    a3Badge && totalStatistics > 0
      ? {
          id: 'a3-badge',
          title: <div className="mt-6">{tcommon('a3_badge')}</div>,
          content: (
            <div className="space-y-4 mb-4">
              <div className="flex items-center gap-4">
                {a3Badge && (
                  <Image src={a3Badge} alt="a3 badge" width={64} height={64} />
                )}
                {/* <div className="flex flex-col gap-1">
                  <div className="text-gray-700">
                    {tcommon('a3_number_of_a3s')}
                  </div>
                  <div className="text-2xl font-medium text-gray-900">
                    {totalStatistics}
                  </div>
                </div> */}
              </div>
              {/* <div className="grid grid-cols-2 gap-4 divide-x-2 divide-gray-200">
                <div className="flex flex-col gap-1">
                  <div className="text-gray-700">{tcommon('a3_owned')}</div>
                  <div className="text-2xl font-medium text-gray-900">
                    {a3StatisticsData?.getA3StatisticsForUser.owned || 0}
                  </div>
                </div>

                <div className="flex flex-col gap-1 pl-3">
                  <div className="text-gray-700">{tcommon('a3_sponsored')}</div>
                  <div className="text-2xl font-medium text-gray-900">
                    {a3StatisticsData?.getA3StatisticsForUser.sponsored || 0}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4 divide-x-2 divide-gray-200">
                <div className="flex flex-col gap-1">
                  <div className="text-gray-700">{tcommon('a3_coached')}</div>
                  <div className="text-2xl font-medium text-gray-900">
                    {a3StatisticsData?.getA3StatisticsForUser.coached || 0}
                  </div>
                </div>

                <div className="flex flex-col gap-1 pl-3">
                  <div className="text-gray-700">
                    {tcommon('a3_teammember')}
                  </div>
                  <div className="text-2xl font-medium text-gray-900">
                    {a3StatisticsData?.getA3StatisticsForUser.member || 0}
                  </div>
                </div>
              </div> */}
              <Divider />
            </div>
          ),
        }
      : null,
    {
      id: 'short-bio',
      title: tcommon('user_profile_biography'),
      content: data?.findUser.profile?.shortBio ? (
        <span
          className="text-gray-900 font-normal"
          data-test="member-profile-short-bio"
        >
          {data.findUser.profile.shortBio}
        </span>
      ) : (
        <div className="text-gray-700">{tcommon('no_information')}</div>
      ),
    },
    {
      id: 'skills',
      title: tcommon('user_profile_skills'),
      content: (
        <Row className="gap-2 mt-3" data-test="member-profile-skills">
          {data?.findUser?.skills.length ? (
            data.findUser.skills.map((skill, index) => {
              return (
                <Col
                  className="bg-gray-200 py-1 px-3 rounded-full text-xs"
                  key={index}
                >
                  {skill.name}
                </Col>
              );
            })
          ) : (
            <div className="text-gray-700">{tcommon('no_information')}</div>
          )}
        </Row>
      ),
    },
    {
      id: 'topics',
      title: tcommon('user_profile_interested_topics'),
      content: (
        <Row
          className="gap-2 mt-3"
          data-test="member-profile-interested-topics"
        >
          {data?.findUser?.topics.length ? (
            data.findUser.topics.map((topic, index) => {
              return (
                <Col
                  className="bg-gray-200 py-1 px-3 rounded-full text-xs"
                  key={index}
                >
                  {topic.name}
                </Col>
              );
            })
          ) : (
            <div className="text-gray-700">{tcommon('no_information')}</div>
          )}
        </Row>
      ),
    },
    {
      id: 'region',
      title: tcommon('user_profile_interested_region'),
      content: (
        <Row
          className="gap-2 mt-3"
          data-test="member-profile-interested-region"
        >
          {data?.findUser?.regions.length ? (
            data.findUser.regions.map((region, index) => {
              return (
                <Col
                  className="bg-gray-200 py-1 px-3 rounded-full text-xs"
                  key={index}
                >
                  {region.name}
                </Col>
              );
            })
          ) : (
            <div className="text-gray-700">{tcommon('no_information')}</div>
          )}
        </Row>
      ),
    },
    {
      id: 'company',
      title: tcommon('user_profile_interested_company'),
      content: (
        <div
          className="space-y-4"
          data-test="member-profile-interested-company"
        >
          {companies?.length ? (
            companies.map((company, index) => {
              return (
                <div key={index} className="bg-gray-200 p-4 rounded-xl">
                  <div className="flex gap-3">
                    <CompanyAvatar
                      size={64}
                      company={company}
                      showBadge={company.companyPortfolio?.isScvPortfolio}
                    />
                    <div className="w-full">
                      <Link href={getCompanyLink(company.id)}>
                        <Text className="!text-base !text-gray-900">
                          {company.name}
                        </Text>
                      </Link>
                      <span className="line-clamp-2">
                        {company.description}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center">
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
            </div>
          )}
        </div>
      ),
    },
  ].filter((v) => v !== null);

  if (loading) {
    return (
      <div className="text-center">
        <Spin />
      </div>
    );
  }

  return (
    <div className="col-span-12 flex flex-col justify-center items-center gap-4">
      {data?.findUser && <UserHeaderSection data={data?.findUser} />}
      <div className="w-full">
        <div className="space-y-2">
          <div className="flex gap-2">
            {email && (
              <Button
                shape="circle"
                href={`mailto:${email}`}
                data-test="member-email-btn"
              >
                <MailOutlined />
              </Button>
            )}
            {!data?.findUser.profile?.socialHidden && (
              <>
                {data?.findUser.profile?.facebook && (
                  <Button
                    shape="circle"
                    href={data?.findUser.profile?.facebook}
                    target="_blank"
                    data-test="member-facebook-btn"
                  >
                    <FacebookFilled />
                  </Button>
                )}
                {data?.findUser.profile?.twitter && (
                  <Button
                    shape="circle"
                    href={data?.findUser.profile?.twitter}
                    target="_blank"
                    data-test="member-twitter-btn"
                  >
                    <TwitterOutlined />
                  </Button>
                )}
                {data?.findUser.profile?.linkedin && (
                  <Button
                    shape="circle"
                    href={data?.findUser.profile?.linkedin}
                    target="_blank"
                    data-test="member-linkedin-btn"
                  >
                    <LinkedinFilled />
                  </Button>
                )}
                {data?.findUser.profile?.instagram && (
                  <Button
                    shape="circle"
                    href={data?.findUser.profile?.instagram}
                    target="_blank"
                    data-test="member-instagram-btn"
                  >
                    <InstagramFilled />
                  </Button>
                )}
                {data?.findUser.profile?.medium && (
                  <Button
                    shape="circle"
                    href={data?.findUser.profile?.medium}
                    target="_blank"
                    data-test="member-medium-btn"
                  >
                    <MediumCircleFilled />
                  </Button>
                )}
              </>
            )}
          </div>
          {items.map((item, index) => (
            <div
              key={index}
              className="space-y-2"
              data-test={`member-content-${item.id}`}
            >
              <div className="text-gray-900 font-semibold">{item.title}</div>
              <div>{item.content}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default UserInfo;
